import { useId } from "react";
import type { StellarStage, StellarState } from "../shared/stellar-types";
import { hrPosition, STELLAR_STAGES } from "../stellar-engine";

const stageOrder: StellarStage[] = ["cloud", "protostar", "mainSequence", "giant", "terminal", "remnant"];

export function StellarCrossSection({ state, pulseEcho }: { state: StellarState; pulseEcho: number }) {
  const titleId = useId();
  const descriptionId = useId();
  const gradientId = useId().replaceAll(":", "");
  const star = state.star;
  const radius = ({ cloud: 68, protostar: 80, mainSequence: 88, giant: 112, terminal: 98, remnant: 38 } satisfies Record<StellarStage, number>)[state.stage];
  const color = stellarColor(star?.surfaceTemperatureK ?? 15, state.stage);
  const composition = star?.composition;

  return (
    <figure className="stellar-figure stellar-cross-section" data-evidence="stellar-cross-section">
      <figcaption>
        <span>恒星剖面</span>
        <strong>{star ? stageName(state) : "等待恒星胚胎"}</strong>
      </figcaption>
      <svg viewBox="0 0 420 300" role="img" aria-labelledby={`${titleId} ${descriptionId}`}>
        <title id={titleId}>{star ? `${stageName(state)}剖面示意` : "尚未形成恒星"}</title>
        <desc id={descriptionId}>
          {star
            ? `表面温度 ${formatValue(star.surfaceTemperatureK)} 开尔文，光度为太阳的 ${formatValue(star.luminositySolar)} 倍。层厚为阶段示意，不代表真实比例。`
            : "完成基础模型并选择质量后，这里会显示同一颗恒星随阶段变化的结构。"}
        </desc>
        <defs>
          <radialGradient id={gradientId}>
            <stop offset="0" stopColor="#fffdf2" />
            <stop offset=".42" stopColor={color} />
            <stop offset="1" stopColor={color} stopOpacity=".5" />
          </radialGradient>
        </defs>
        <g className="stellar-blueprint" aria-hidden="true">
          <circle cx="210" cy="144" r="126" />
          <circle cx="210" cy="144" r="92" />
          <path d="M48 144H372M210 12V276" />
        </g>
        {star ? (
          <g>
            <circle className="stellar-halo" cx="210" cy="144" r={radius + 22} fill={color} />
            <circle cx="210" cy="144" r={radius} fill={`url(#${gradientId})`} className="stellar-body" />
            {composition && (
              <g className="stellar-core-layers" aria-hidden="true">
                <circle cx="210" cy="144" r={Math.max(12, radius * .5)} fill="#ffbc71" opacity={.24 + composition.helium * .5} />
                <circle cx="210" cy="144" r={Math.max(8, radius * .28)} fill="#eef5ff" opacity={.2 + composition.carbonOxygen * .75} />
                {composition.silicon > .02 && <circle cx="210" cy="144" r={Math.max(5, radius * .14)} fill="#b7d8ff" opacity=".86" />}
                {composition.iron > .04 && <circle cx="210" cy="144" r={Math.max(3, radius * .07)} fill="#d8e2e8" opacity=".95" />}
              </g>
            )}
            {pulseEcho > 0 && (
              <g key={pulseEcho} className="stellar-pulse" aria-hidden="true">
                <circle cx="210" cy="144" r={radius + 4} />
                <circle cx="210" cy="144" r={radius + 12} />
              </g>
            )}
          </g>
        ) : (
          <g className="stellar-cloud" aria-hidden="true">
            <circle cx="177" cy="137" r="48" />
            <circle cx="229" cy="124" r="58" />
            <circle cx="225" cy="169" r="51" />
          </g>
        )}
        <g className="stellar-callouts" aria-hidden="true">
          <path d="M210 144L322 78H374" />
          <text x="374" y="74" textAnchor="end">表面</text>
          <text x="374" y="89" textAnchor="end">{star ? `${formatValue(star.surfaceTemperatureK)} K` : "冷气体"}</text>
          <path d="M210 144L95 214H47" />
          <text x="47" y="211">核心</text>
          <text x="47" y="226">{coreLabel(state)}</text>
        </g>
      </svg>
      <small>剖面是保留燃料顺序的阶段化示意；真实恒星没有可见的硬质分层边界。</small>
    </figure>
  );
}

export function HrDiagram({
  temperatureK,
  luminositySolar,
  pointLabel = "当前恒星",
}: {
  temperatureK?: number;
  luminositySolar?: number;
  pointLabel?: string;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const position = temperatureK !== undefined && luminositySolar !== undefined
    ? plotPosition(temperatureK, luminositySolar)
    : null;

  return (
    <figure className="stellar-figure stellar-hr" data-evidence="hr-diagram">
      <figcaption><span>H–R 图</span><strong>温度 × 光度轨迹</strong></figcaption>
      <svg viewBox="0 0 440 300" role="img" aria-labelledby={`${titleId} ${descriptionId}`}>
        <title id={titleId}>赫罗图温度与光度位置</title>
        <desc id={descriptionId}>
          横轴从左到右由热变冷，纵轴向上光度更高。
          {position ? `${pointLabel}位于温度 ${formatValue(temperatureK!)} 开尔文、太阳光度 ${formatValue(luminositySolar!)}。` : "当前没有可定位样本。"}
        </desc>
        <g className="hr-grid" aria-hidden="true">
          {[36, 94, 152, 210, 268].map((y) => <path key={`y-${y}`} d={`M60 ${y}H414`} />)}
          {[60, 148, 236, 324, 412].map((x) => <path key={`x-${x}`} d={`M${x} 26V268`} />)}
        </g>
        <g className="hr-regions" aria-hidden="true">
          <path className="hr-main-band" d="M82 48 C145 76 205 111 265 159 S356 222 397 245" />
          <path className="hr-giant-region" d="M258 42 Q370 30 407 101 Q347 121 278 95Z" />
          <path className="hr-dwarf-region" d="M72 194 Q145 185 204 239 Q143 265 78 252Z" />
          <text x="305" y="71">巨星区</text>
          <text x="210" y="134" transform="rotate(32 210 134)">主序带</text>
          <text x="89" y="230">白矮星区</text>
        </g>
        <g className="hr-axes" aria-hidden="true">
          <path d="M60 26V268H414" />
          <text x="60" y="287">30,000 K</text>
          <text x="236" y="287" textAnchor="middle">10,000 K</text>
          <text x="414" y="287" textAnchor="end">3,000 K</text>
          <text x="238" y="299" textAnchor="middle">表面温度 ← 左热 · 右冷 →</text>
          <text x="12" y="44">10⁵ L☉</text>
          <text x="24" y="157">1 L☉</text>
          <text x="12" y="263">10⁻³ L☉</text>
          <text x="14" y="18">更亮 ↑</text>
        </g>
        <g className="hr-sun" aria-hidden="true"><circle cx="352" cy="152" r="3" /><text x="360" y="148">太阳</text></g>
        {position && (
          <g className="hr-current-point">
            <circle cx={position.x} cy={position.y} r="13" aria-hidden="true" />
            <circle cx={position.x} cy={position.y} r="5" aria-hidden="true" />
            <text x={Math.min(position.x + 10, 370)} y={Math.max(position.y - 12, 36)}>{pointLabel}</text>
          </g>
        )}
      </svg>
      <dl className="stellar-evidence-pair">
        <div><dt>横轴</dt><dd>左热右冷</dd></div>
        <div><dt>纵轴</dt><dd>向上更亮</dd></div>
        <div><dt>样本</dt><dd>{position ? `${formatValue(temperatureK!)} K · ${formatValue(luminositySolar!)} L☉` : "等待观测"}</dd></div>
      </dl>
    </figure>
  );
}

export function StageTrack({ stage }: { stage: StellarStage }) {
  const current = stageOrder.indexOf(stage);
  return (
    <ol className="stellar-stage-track" aria-label="恒星演化阶段">
      {stageOrder.map((item, index) => (
        <li key={item} data-stage={item} data-status={index < current ? "complete" : index === current ? "current" : "future"}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <b>{STELLAR_STAGES[item].name}</b>
        </li>
      ))}
    </ol>
  );
}

function plotPosition(temperatureK: number, luminositySolar: number) {
  const normalized = hrPosition(temperatureK, luminositySolar);
  return { x: 60 + normalized.x / 100 * 354, y: 26 + normalized.y / 100 * 242 };
}

function stellarColor(temperatureK: number, stage: StellarStage) {
  if (stage === "cloud") return "#776eb1";
  if (temperatureK >= 12_000) return "#9ac8ff";
  if (temperatureK >= 7_500) return "#d7e9ff";
  if (temperatureK >= 5_000) return "#fff1c6";
  if (temperatureK >= 3_200) return "#ffad67";
  return "#df7651";
}

function coreLabel(state: StellarState) {
  if (!state.star) return "尚未形成";
  if (state.stage === "cloud") return "冷分子气体";
  if (state.stage === "protostar") return "收缩升温";
  if (state.stage === "mainSequence") return "氢 → 氦";
  if (state.stage === "giant") return state.star.fate === "coreCollapse" ? "高级燃烧" : "氦 → 碳氧";
  if (state.stage === "terminal") return state.star.fate === "coreCollapse" ? "铁核心" : "碳氧核心";
  return state.star.fate === "coreCollapse" ? "中子星" : "白矮星";
}

function stageName(state: StellarState) {
  if (state.stage === "giant" && state.star?.massId === "titan") return "红超巨星";
  if (state.stage === "terminal") return state.star?.fate === "coreCollapse" ? "核心坍缩" : "外层抛射";
  if (state.stage === "remnant") return state.star?.fate === "coreCollapse" ? "中子星遗迹" : "白矮星遗迹";
  return STELLAR_STAGES[state.stage].name;
}

function formatValue(value: number) {
  return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: value < 1 ? 4 : 1 }).format(value);
}
